// src/app/features/dashboard/services/dashboard-notifications-store.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationService } from 'src/app/features/dashboard/services/notification.service';
import { SessionScopedState } from './session/session-scoped-state';

type NotificationItem = {
  id: string;
  patientId: string;
  startTime: string;
  patient: {
    id: number;
    user: {
      id: number;
      firstName: string;
      lastName: string;
    };
  };
};

type PaginationMeta = {
  currentPage: number;
  itemCount: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
};

export type Notifications = {
  meta: PaginationMeta;
  items: NotificationItem[];
};

export type AppNotificationsStore = {
  isLoading: boolean;
  isRefreshing: boolean;
  notifications: Notifications;
};

const DEFAULT_VALUE = {
  isLoading: false,
  isRefreshing: false,
  notifications: {
    items: [] as NotificationItem[],
    meta: {} as PaginationMeta,
  },
};

export interface DashboardNotificationsResponse {
  notifications: Notifications[];
}

@Injectable({
  providedIn: 'root',
})
export class AppNotificationsStoreStoreService implements SessionScopedState {
  constructor(private notificationService: NotificationService) {}
  private notificationsSubject = new BehaviorSubject<AppNotificationsStore>(
    DEFAULT_VALUE
  );
  public notifications$: Observable<AppNotificationsStore> =
    this.notificationsSubject.asObservable();

  /**
   * Back to the empty state, so one user's notifications are never the first
   * thing the next one sees while their own are still being fetched.
   */
  public clearSessionState(): void {
    this.notificationsSubject.next(DEFAULT_VALUE);
  }

  setNotifications(notifications: AppNotificationsStore): void {
    this.notificationsSubject.next(notifications);
  }

  refreshNotifications(silent): void {
    if (!silent) {
      this.notificationsSubject.next({
        ...this.notificationsSubject.value,
        isLoading: true,
        isRefreshing: true,
      });
    }
    this.notificationsSubject.next({
        ...this.notificationsSubject.value,
        isLoading: true,
      });
    this.notificationService.getNotifications().subscribe({
      next: (res: any) => {
        this.notificationsSubject.next({
          notifications: res,
          isLoading: false,
          isRefreshing: false,
        });
      },
      error: (res) => {
        this.notificationsSubject.next({
          notifications: {
            items: [],
            meta: {
              currentPage: 0,
              itemCount: 0,
              itemsPerPage: 0,
              totalItems: 0,
              totalPages: 0,
            },
          },
          isLoading: false,
          isRefreshing: false,
        });
      },
    });
  }

  getNotificationsSnapshot(): AppNotificationsStore {
    return this.notificationsSubject.value;
  }

  clear(): void {
    this.notificationsSubject.next(DEFAULT_VALUE);
  }
}
